/**
 * Product-side quantity-pricing assignment (Melissa 2026-08-28: "Quantity
 * Pricing should be able to select in the product for customer or not").
 *
 * Three controls, one invariant:
 * - 量價來源 source: follow a reusable SCHEDULE · carry the product's OWN
 *   brackets · none. The XOR (schedule vs ad-hoc, never both) is enforced by
 *   the shape of this control — you pick one radio, so both can never be set.
 * - 排程 schedule select (only in schedule mode), showing each schedule's
 *   apply period so a dormant one is visible at the point of choosing.
 * - 適用對象 scope: all customers · card holders only.
 *
 * DETACH, never overlay (FABLE C2 A1): switching from a schedule to own
 * brackets COPIES the schedule's brackets and drops the reference, and the UI
 * names the cost ("no longer follows QPS-STD"). NetSuite permits
 * schedule-generated cells alongside manual entry; we deliberately do not —
 * a hybrid is a second store of truth behind a pure resolver.
 *
 * The customer-scope control has NO industry precedent (the wave found none
 * in NetSuite, SAP, Odoo or D365) — it is an original SenditSell design and
 * is recorded as such, not dressed up as a pattern.
 *
 * S2.5 (sealed §2.2 build gap): the ad-hoc set is EDITABLE — an inline
 * bracket grid mirroring ScheduleEditorModal (add/insert/remove rows,
 * duplicate-free thresholds, percent-only 0–100, sort-on-save, DA-8
 * warn-only ≥100% banner). Grid model + XOR commit builder live in
 * `./adHocBrackets` so the node vitest env pins them without a mount.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { pricingAsOf, usePricingDataset, usePricingMutations } from '@/hooks/usePricing';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import type {
  ProductPricingFields,
  QuantityBracket,
  QuantityPricingScope,
} from '@/types/api/pricing';
import {
  buildQuantityPricingSaveFields,
  copyScheduleBrackets,
  hasAdHocBracketIssues,
  hasFullDiscountBracket,
  validateAdHocBrackets,
  type SourceMode,
} from './adHocBrackets';

const { Text } = Typography;

export interface EditQuantityPricingModalProps {
  open: boolean;
  productId: string;
  onCancel: () => void;
  onSaved: () => void;
}

export const EditQuantityPricingModal = ({
  open,
  productId,
  onCancel,
  onSaved,
}: EditQuantityPricingModalProps) => {
  const { t, i18n } = useTranslation(['pricing', 'products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { dataset } = usePricingDataset();
  const { saveProductPricing } = usePricingMutations();
  const zh = i18n.language.startsWith('zh');

  const fields = useMemo<ProductPricingFields | undefined>(
    () => dataset?.productPricing?.find((row) => row.productId === productId),
    [dataset, productId]
  );
  const schedules = useMemo(
    () => (dataset?.quantityPricingSchedules ?? []).filter((schedule) => schedule.isActive),
    [dataset]
  );

  const initialMode: SourceMode = fields?.quantityScheduleId
    ? 'schedule'
    : (fields?.quantityBrackets?.length ?? 0) > 0
      ? 'ad_hoc'
      : 'none';

  const [mode, setMode] = useState<SourceMode>(initialMode);
  const [scheduleId, setScheduleId] = useState<string | null>(fields?.quantityScheduleId ?? null);
  const [adHoc, setAdHoc] = useState<QuantityBracket[]>(fields?.quantityBrackets ?? []);
  const [scope, setScope] = useState<QuantityPricingScope>(fields?.quantityScope ?? 'all_customers');
  const [reasonOpen, setReasonOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setScheduleId(fields?.quantityScheduleId ?? null);
    setAdHoc(fields?.quantityBrackets ?? []);
    setScope(fields?.quantityScope ?? 'all_customers');
  }, [open, initialMode, fields]);

  /** The schedule currently referenced BEFORE this edit — the detach source. */
  const previousSchedule = useMemo(
    () =>
      fields?.quantityScheduleId
        ? (dataset?.quantityPricingSchedules ?? []).find(
            (schedule) => schedule.id === fields.quantityScheduleId
          ) ?? null
        : null,
    [dataset, fields]
  );

  /** Switching schedule → own brackets is a CONVERSION: copy, then detach. */
  const detachToAdHoc = () => {
    if (previousSchedule && adHoc.length === 0) {
      setAdHoc(copyScheduleBrackets(previousSchedule.brackets));
    }
    setMode('ad_hoc');
  };

  // S2.5 grid model — validation is per-render over the raw entry order;
  // save is blocked while any row is flagged (mirrors ScheduleEditorModal's
  // resolver-blocked submit). The ≥100% banner is warn-only (DA-8).
  const bracketIssues = useMemo(() => validateAdHocBrackets(adHoc), [adHoc]);
  const bracketsInvalid = mode === 'ad_hoc' && hasAdHocBracketIssues(bracketIssues);
  const fullDiscount = mode === 'ad_hoc' && hasFullDiscountBracket(adHoc);

  const setBracketAt = (index: number, patch: Partial<QuantityBracket>) =>
    setAdHoc((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const appendBracket = () =>
    setAdHoc((rows) => {
      const nextMin =
        rows.length === 0 ? 1 : Math.max(...rows.map((bracket) => bracket.minQuantity)) + 10;
      return [...rows, { minQuantity: nextMin, discountPct: 0 }];
    });

  const insertBracketBelow = (index: number) =>
    setAdHoc((rows) => {
      const here = rows[index]?.minQuantity ?? 1;
      const next = rows[index + 1]?.minQuantity;
      // Midpoint when integral, else one step above (then the on-save sort +
      // duplicate check keeps the ladder honest) — ScheduleEditorModal seed.
      const seed =
        next !== undefined && next - here > 1 ? Math.floor((here + next) / 2) : here + 10;
      return [...rows.slice(0, index + 1), { minQuantity: seed, discountPct: 0 }, ...rows.slice(index + 1)];
    });

  const removeBracketAt = (index: number) =>
    setAdHoc((rows) => rows.filter((_, i) => i !== index));

  // No index-0 pin here (unlike ScheduleEditorModal): ad-hoc rows are
  // DISCOUNTS off a resolved base — a qty below the lowest threshold falls
  // back to the base price, never to no-price, so a >1 first threshold is a
  // legitimate state (the shipped seed starts at 5).
  const bracketColumns: TableProps<QuantityBracket>['columns'] = [
    {
      title: t('pricing:pricing.scheduleEditor.columns.minQuantity'),
      key: 'minQuantity',
      width: 200,
      render: (_: unknown, row, index) => (
        <InputNumber
          value={row.minQuantity}
          onChange={(next) => setBracketAt(index, { minQuantity: next ?? 1 })}
          min={1}
          step={1}
          size="middle"
          status={
            bracketIssues.duplicateIndexes.includes(index) ||
            bracketIssues.outOfRangeIndexes.includes(index)
              ? 'error'
              : undefined
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('pricing:pricing.scheduleEditor.columns.discountPct'),
      key: 'discountPct',
      width: 200,
      render: (_: unknown, row, index) => (
        <InputNumber
          value={row.discountPct}
          onChange={(next) => setBracketAt(index, { discountPct: next ?? 0 })}
          min={0}
          max={100}
          step={1}
          size="middle"
          addonAfter="%"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_: unknown, __, index) => (
        <>
          <Button
            type="text"
            aria-label={t('pricing:pricing.scheduleEditor.insertBelow')}
            icon={<PlusOutlined />}
            onClick={() => insertBracketBelow(index)}
          />
          <Button
            type="text"
            aria-label={t('pricing:pricing.scheduleEditor.removeBracket')}
            icon={<DeleteOutlined />}
            onClick={() => removeBracketAt(index)}
          />
        </>
      ),
    },
  ];

  const scheduleOptions = schedules.map((schedule) => {
    const period =
      !schedule.appliesFrom && !schedule.appliesTo
        ? t('pricing:pricing.schedulesAdmin.periodAlways')
        : `${schedule.appliesFrom ?? t('pricing:pricing.schedulesAdmin.periodOpenStart')} — ${
            schedule.appliesTo ?? t('pricing:pricing.schedulesAdmin.periodOpenEnd')
          }`;
    const today = pricingAsOf();
    const dormant =
      (!!schedule.appliesFrom && today < schedule.appliesFrom) ||
      (!!schedule.appliesTo && today > schedule.appliesTo);
    return {
      value: schedule.id,
      label: `${schedule.code} · ${zh ? schedule.nameZh : schedule.nameEn} · ${period}${
        dormant ? ` · ${t('pricing:pricing.productSection.quantityCard.dormantSuffix')}` : ''
      }`,
    };
  });

  const commit = (reason: string) => {
    try {
      // XOR by construction + ascending sort-on-save (adHocBrackets.ts).
      saveProductPricing(
        buildQuantityPricingSaveFields(fields, productId, mode, scheduleId, adHoc, scope),
        reason
      );
      handleSuccess(t('pricing:pricing.productSection.quantityCard.saved'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <Modal
        title={t('pricing:pricing.productSection.quantityCard.editTitle')}
        open={open}
        onCancel={onCancel}
        onOk={() => setReasonOpen(true)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
          disabled: (mode === 'schedule' && !scheduleId) || bracketsInvalid,
        }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        width={640}
        destroyOnHidden
      >
        <div style={{ display: 'grid', gap: token.marginMD }}>
          <div>
            <Text style={formStyles.labelStyle}>
              {t('pricing:pricing.productSection.quantityCard.sourceLabel')}
            </Text>
            <Radio.Group
              value={mode}
              onChange={(event) => {
                const next = event.target.value as SourceMode;
                if (next === 'ad_hoc') detachToAdHoc();
                else setMode(next);
              }}
            >
              <Space direction="vertical">
                <Radio value="schedule">
                  {t('pricing:pricing.productSection.quantityCard.sourceSchedule')}
                </Radio>
                <Radio value="ad_hoc">
                  {t('pricing:pricing.productSection.quantityCard.sourceAdHoc')}
                </Radio>
                <Radio value="none">
                  {t('pricing:pricing.productSection.quantityCard.sourceNone')}
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          {mode === 'schedule' && (
            <div>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.productSection.quantityCard.scheduleLabel')}
              </Text>
              <Select
                value={scheduleId ?? undefined}
                onChange={setScheduleId}
                options={scheduleOptions}
                size="large"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder={t('pricing:pricing.productSection.quantityCard.schedulePlaceholder')}
              />
            </div>
          )}

          {/* The detach cost, named (C2 A1) — never a silent conversion. */}
          {mode === 'ad_hoc' && previousSchedule && (
            <Alert
              type="warning"
              showIcon
              message={t('pricing:pricing.productSection.quantityCard.detachTitle', {
                code: previousSchedule.code,
              })}
              description={t('pricing:pricing.productSection.quantityCard.detachHint', {
                count: adHoc.length,
              })}
            />
          )}

          {/* S2.5 — the ad-hoc set is authored HERE (detach-copied or from
              empty); the schedule reference stays read-only in schedule mode. */}
          {mode === 'ad_hoc' && (
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <Text style={formStyles.labelStyle}>
                  {t('pricing:pricing.scheduleEditor.bracketsTitle')}
                </Text>
                <Button icon={<PlusOutlined />} onClick={appendBracket}>
                  {t('pricing:pricing.scheduleEditor.addBracket')}
                </Button>
              </div>
              {fullDiscount && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('pricing:pricing.warnings.discountZeroPrice')}
                  style={{ marginBottom: token.marginXS }}
                />
              )}
              <Table<QuantityBracket>
                columns={bracketColumns}
                dataSource={adHoc}
                rowKey={(_, index) => index ?? 0}
                size="small"
                pagination={false}
                locale={{
                  emptyText: (
                    <Text type="secondary">
                      {t('pricing:pricing.productSection.quantityCard.noAdHocBrackets')}
                    </Text>
                  ),
                }}
              />
              {bracketIssues.duplicateIndexes.length > 0 && (
                <Text style={formStyles.errorStyle}>
                  {t('pricing:pricing.scheduleEditor.validation.duplicateQuantity')}
                </Text>
              )}
            </div>
          )}

          <div>
            <Text style={formStyles.labelStyle}>
              {t('pricing:pricing.productSection.quantityCard.scopeLabel')}
            </Text>
            <Radio.Group value={scope} onChange={(event) => setScope(event.target.value)}>
              <Space direction="vertical">
                <Radio value="all_customers">
                  {t('pricing:pricing.productSection.quantityCard.scopeAll')}
                </Radio>
                <Radio value="card_holders">
                  {t('pricing:pricing.productSection.quantityCard.scopeCardHolders')}
                </Radio>
              </Space>
            </Radio.Group>
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 4, fontSize: token.fontSizeSM }}
            >
              {t('pricing:pricing.productSection.quantityCard.scopeHint')}
            </Text>
          </div>
        </div>
      </Modal>

      <ReasonRequiredModal
        open={reasonOpen}
        title={t('pricing:pricing.productSection.quantityCard.reason.title')}
        description={t('pricing:pricing.productSection.quantityCard.reason.description')}
        reasonLabel={t('pricing:pricing.productSection.quantityCard.reason.label')}
        reasonPlaceholder={t('pricing:pricing.productSection.quantityCard.reason.placeholder')}
        requiredMessage={t('pricing:pricing.productSection.quantityCard.reason.required')}
        submitText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        onSubmit={(reason) => {
          setReasonOpen(false);
          commit(reason);
        }}
        onCancel={() => setReasonOpen(false)}
      />
    </>
  );
};

export default EditQuantityPricingModal;
